import { DateTime } from "luxon";

export const GOOGLE_ADS_FIELDS = ["googleClickId", "googleWbraid", "googleGbraid"] as const;
export type GoogleAdsAttribution = Partial<Record<(typeof GOOGLE_ADS_FIELDS)[number], string>>;
export interface GoogleAdsAttributionBundle extends GoogleAdsAttribution {
  capturedAt: string;
}
/** Transport metadata only; never add this timestamp to lead snapshot tracking. */
export interface GoogleAdsCapture extends GoogleAdsAttribution {
  googleAdsCapturedAt: string;
}
export const GOOGLE_ADS_WINDOW_DAYS = 90;
export const GOOGLE_ADS_CLOCK_SKEW_MINUTES = 5;
export const GOOGLE_ADS_MAX_VALUE_LENGTH = 512;
export const GOOGLE_ADS_MAX_COOKIE_LENGTH = 3500;

export function normalizeTrackingId(value: unknown): string | undefined {
  return typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= GOOGLE_ADS_MAX_VALUE_LENGTH &&
    !/[\x00-\x1f\x7f]/.test(value)
    ? value
    : undefined;
}

/** Select only valid named IDs. Never change identifier case or combine sources. */
export function normalizeGoogleAdsAttribution(input: unknown): GoogleAdsAttribution | undefined {
  if (!input || typeof input !== "object" || Array.isArray(input)) return undefined;
  const source = input as Record<string, unknown>;
  const result: GoogleAdsAttribution = {};
  for (const field of GOOGLE_ADS_FIELDS) {
    const value = normalizeTrackingId(source[field]);
    if (value) result[field] = value;
  }
  return hasGoogleAdsAttribution(result) ? result : undefined;
}

export function hasGoogleAdsAttribution(input: GoogleAdsAttribution | undefined): boolean {
  return GOOGLE_ADS_FIELDS.some((key) => !!input?.[key]);
}

export function equalGoogleAdsAttribution(
  a: GoogleAdsAttribution | undefined,
  b: GoogleAdsAttribution | undefined
): boolean {
  return GOOGLE_ADS_FIELDS.every((key) => a?.[key] === b?.[key]);
}

export function parseGoogleAdsAttribution(
  search: string | URLSearchParams
): GoogleAdsAttribution | undefined {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return normalizeGoogleAdsAttribution({
    googleClickId: params.get("gclid"),
    googleWbraid: params.get("wbraid"),
    googleGbraid: params.get("gbraid"),
  });
}

export function deserializeGoogleAdsAttribution(
  raw: string | undefined
): GoogleAdsAttributionBundle | undefined {
  if (!raw || raw.length > GOOGLE_ADS_MAX_COOKIE_LENGTH) return undefined;
  let candidate = raw;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      const ids = normalizeGoogleAdsAttribution(parsed);
      if (
        !ids ||
        !parsed ||
        typeof parsed !== "object" ||
        !("capturedAt" in parsed) ||
        typeof parsed.capturedAt !== "string"
      )
        return undefined;
      const time = DateTime.fromISO(parsed.capturedAt);
      const now = DateTime.utc();
      if (
        !time.isValid ||
        time > now.plus({ minutes: GOOGLE_ADS_CLOCK_SKEW_MINUTES }) ||
        time < now.minus({ days: GOOGLE_ADS_WINDOW_DAYS })
      )
        return undefined;
      return { ...ids, capturedAt: parsed.capturedAt };
    } catch {
      // Cookie readers may return raw JSON or the URI-encoded wire value.
      // Decode only after parsing fails, preserving literal percent sequences.
      try {
        candidate = decodeURIComponent(raw);
      } catch {
        return undefined;
      }
    }
  }
  return undefined;
}

/** Validate transport age, retaining capture time independently from the IDs. */
export function normalizeGoogleAdsCapture(input: unknown): GoogleAdsCapture | undefined {
  const ids = normalizeGoogleAdsAttribution(input);
  if (!ids) return undefined;
  const source = input as Record<string, unknown>;
  const capturedAt = source.googleAdsCapturedAt ?? source.capturedAt ?? DateTime.utc().toISO();
  const bundle = deserializeGoogleAdsAttribution(JSON.stringify({ ...ids, capturedAt }));
  return bundle ? { ...ids, googleAdsCapturedAt: bundle.capturedAt } : undefined;
}

export function serializeGoogleAdsAttribution(
  bundle: GoogleAdsAttributionBundle
): string | undefined {
  const normalized = deserializeGoogleAdsAttribution(JSON.stringify(bundle));
  if (!normalized) return undefined;
  const raw = JSON.stringify(normalized);
  return encodeURIComponent(raw).length <= GOOGLE_ADS_MAX_COOKIE_LENGTH ? raw : undefined;
}

/** Mutates only the Google slice; absent incoming capture preserves the old bundle. */
export function replaceGoogleAdsSlice(target: Record<string, unknown>, source: unknown): boolean {
  const incoming = normalizeGoogleAdsAttribution(source);
  if (!incoming || equalGoogleAdsAttribution(normalizeGoogleAdsAttribution(target), incoming))
    return false;
  for (const field of GOOGLE_ADS_FIELDS) delete target[field];
  Object.assign(target, incoming);
  return true;
}
